import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, RefreshCw, LayoutDashboard, ServerCrash, FileQuestion } from 'lucide-react'

const CONFIG = {
  404: {
    icon:    FileQuestion,
    color:   'text-blue-400',
    bg:      'bg-blue-500/10',
    ring:    'ring-blue-500/20',
    badge:   'bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20',
    title:   'Page Not Found',
    sub:     "This page vanished into the void. Subho probably deleted it by accident.",
    canRetry: false,
  },
  500: {
    icon:    ServerCrash,
    color:   'text-red-400',
    bg:      'bg-red-500/10',
    ring:    'ring-red-500/20',
    badge:   'bg-red-500/10 text-red-400 ring-1 ring-red-500/20',
    title:   'Server Error',
    sub:     "Something exploded on the backend. Subho is probably aware. Probably.",
    canRetry: true,
  },
}

const AdminError = ({ code = 404 }) => {
  const navigate  = useNavigate()
  const cfg       = CONFIG[code] || CONFIG[404]
  const Icon      = cfg.icon

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-primary-600/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-red-600/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative text-center max-w-md w-full"
      >
        {/* Icon blob */}
        <div className={`inline-flex w-20 h-20 rounded-2xl ${cfg.bg} ring-1 ${cfg.ring} items-center justify-center mb-6`}>
          <Icon className={`w-9 h-9 ${cfg.color}`} />
        </div>

        {/* Code badge */}
        <div className="flex justify-center mb-4">
          <span className={`text-xs font-bold px-3 py-1 rounded-full tracking-widest uppercase ${cfg.badge}`}>
            Error {code}
          </span>
        </div>

        <h1 className="text-3xl font-extrabold text-white mb-3 tracking-tight">{cfg.title}</h1>
        <p className="text-gray-400 text-sm leading-relaxed mb-8">{cfg.sub}</p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white text-sm font-semibold transition-all w-full sm:w-auto justify-center"
          >
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>

          {cfg.canRetry && (
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 hover:text-red-300 text-sm font-semibold transition-all w-full sm:w-auto justify-center"
            >
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          )}

          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-all w-full sm:w-auto justify-center shadow-lg shadow-primary-900/30"
          >
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default AdminError
